import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Passenger-facing cancellation dialog. Forces the user to pick a documented
 * reason (Voyage annulé / Chauffeur inaccessible / Annulation à la demande du
 * chauffeur / Autres). "Autres" unlocks a free-text field.
 * The admin sees the reason in the dashboard before deciding to refund the
 * driver's TFT commission (feature per user request).
 */
const REASONS = [
  { code: "trip_cancelled",    label: "Voyage annulé",                        icon: "🧳" },
  { code: "driver_unreachable", label: "Chauffeur inaccessible",              icon: "📵" },
  { code: "driver_requested",   label: "Annulation à la demande du chauffeur", icon: "🚗" },
  { code: "other",              label: "Autres",                              icon: "✏️" },
];

export default function CancelBookingDialog({ open, onClose, bookingId, onCancelled }) {
  const [reasonCode, setReasonCode] = useState("");
  const [reasonDetails, setReasonDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setReasonCode("");
    setReasonDetails("");
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const isOther = reasonCode === "other";
  const canSubmit = !!reasonCode && (!isOther || reasonDetails.trim().length >= 5);

  const handleConfirm = async () => {
    if (!canSubmit || !bookingId) return;
    setSubmitting(true);
    try {
      await api.post(`/bookings/${bookingId}/cancel`, {
        reason_code: reasonCode,
        reason_details: isOther ? reasonDetails.trim() : "",
      });
      toast.success("Réservation annulée", {
        description: "Le chauffeur a été notifié du motif.",
      });
      reset();
      onClose?.();
      onCancelled?.();
    } catch (err) {
      toast.error("Impossible d'annuler", {
        description: err.response?.data?.detail || "Erreur inconnue",
      });
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md" data-testid="cancel-booking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
            <XCircle className="w-5 h-5 text-[#EF4444]" />
            Annuler la réservation
          </DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Sélectionnez un motif ci-dessous. Il sera transmis au chauffeur et à l&apos;administrateur.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2" data-testid="cancel-reason-list">
          {REASONS.map((r) => {
            const active = reasonCode === r.code;
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => setReasonCode(r.code)}
                className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition ${
                  active
                    ? "border-[#0A2540] bg-[#0A2540] text-white"
                    : "border-[#E2E8F0] bg-white text-[#0A2540] hover:border-[#0A2540]"
                }`}
                data-testid={`cancel-reason-${r.code}`}
              >
                <span className={`w-9 h-9 rounded-full flex items-center justify-center text-lg shrink-0 ${active ? "bg-[#FACC15]" : "bg-[#F1F5F9]"}`}>
                  {r.icon}
                </span>
                <span className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>{r.label}</span>
                {active && (
                  <span className="ml-auto text-[10px] font-bold text-[#FACC15] uppercase tracking-wider">Sélectionné</span>
                )}
              </button>
            );
          })}
        </div>

        {isOther && (
          <div className="mt-2">
            <label className="block text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1.5 ml-1">
              Précisez le motif
            </label>
            <textarea
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value.slice(0, 500))}
              placeholder="Décrivez brièvement la raison de l'annulation…"
              rows={3}
              className="w-full rounded-2xl border border-[#E2E8F0] p-3 text-sm text-[#0A2540] focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none resize-none"
              data-testid="cancel-reason-details"
            />
            <p className="text-[10px] text-[#94A3B8] mt-1 text-right">{reasonDetails.length}/500</p>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
            className="flex-1 h-12 rounded-full border-[#E2E8F0]"
            data-testid="cancel-dialog-abort"
          >
            Retour
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit || submitting}
            className="flex-1 h-12 rounded-full bg-[#EF4444] text-white hover:bg-[#DC2626] font-bold disabled:opacity-50"
            data-testid="cancel-dialog-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
            Annuler la réservation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
